/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useEffect, useRef, useState } from "react";
import { Loader2, Mic, Square, Save, MessageSquareText, Sparkles, CalendarClock, X } from "lucide-react";
import { toast } from "../../../components/ui/sonner";
import { useRecorder } from "../../../lib/useRecorder";
import { fmtT, fmtD, isClosedProject } from "./meta";
import { T } from "../../../lib/i18n";
/* 【2026-10-30 输入侧时区提示】「下一步」日期是给设计部同事看的节点提示，
   按美国日历日填写（显示侧本来就是这个口径）。版本说明是纯文本，版面窄，用 compact 形态。 */
import { UsDateHint } from "./UsDateHint";

/* 一句话「现状 + 下一步节点安排」：文字 / 语音（录音 → Whisper → AI 整理成一句话 + 抽取下一步日期 → 核对后保存） */
export const StatusBrief = ({ p, meta, api, refresh }) => {
  const [v, setV] = useState(p.statusBrief || "");
  const [nd, setNd] = useState(p.statusBriefNextDate || "");
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [stt, setStt] = useState(false);
  const [polishing, setPolishing] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => { setV(p.statusBrief || ""); setNd(p.statusBriefNextDate || ""); setRaw(""); }, [p.statusBrief, p.statusBriefNextDate]);
  const dirty = v.trim() !== (p.statusBrief || "") || (nd || "") !== (p.statusBriefNextDate || "");
  const applyAi = (data) => {
    setRaw(data.ai && data.polished !== data.text ? data.text : "");
    setV(data.polished || data.text);
    if (data.nextDate) setNd(data.nextDate);
    toast.success(data.ai ? T("AI 已整理为一句话{0}，请核对后保存", [data.nextDate ? T("并识别到下一步 {0}", [fmtD(data.nextDate)]) : ""]) : T("已转写，请核对后保存"));
    setTimeout(() => inputRef.current?.focus(), 50);
  };
  const save = async () => {
    if (!dirty || busy) return;
    setBusy(true);
    try { await api.patch(`/design/v3/projects/${p.id}`, { statusBrief: v.trim(), statusBriefNextDate: v.trim() ? nd || null : null }); toast.success(v.trim() ? T("现状 / 下一步已更新") : T("已清空现状描述")); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const polish = async () => {
    if (v.trim().length < 2) return;
    setPolishing(true);
    try { const { data } = await api.post(`/design/v3/projects/${p.id}/status-brief/polish`, { text: v }, { timeout: 60000 }); applyAi(data); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setPolishing(false);
  };
  const transcribe = async (blob) => {
    if (!blob?.size) return;
    setStt(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, `brief_${Date.now()}.${(blob.type || "").includes("mp4") ? "m4a" : "webm"}`);
      const { data } = await api.post(`/design/v3/projects/${p.id}/status-brief/transcribe`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      applyAi(data);
    } catch (e) { toast.error(e?.response?.data?.detail || T("语音转写失败，请重试或直接输入文字")); }
    setStt(false);
  };
  const { rec, sec, start, stop } = useRecorder({ maxSec: 60, onBlob: transcribe });
  const mic = async () => {
    if (rec) return stop();
    const err = await start();
    if (err === "unsupported") toast.error(T("当前浏览器不支持录音，请直接输入文字"));
    else if (err) toast.error(T("无法访问麦克风，请在浏览器授权后重试"));
  };
  const when = p.statusBriefAt ? `${fmtT(p.statusBriefAt)} · ${p.statusBriefBy || "—"}` : "";
  const lock = busy || stt || polishing;
  if (!meta.canEdit || isClosedProject(p)) {
    return p.statusBrief ? (
      <div className="rounded-lg border border-sky-200 bg-sky-50/60 px-2.5 py-2 text-xs text-slate-800 flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="dv3-brief" data-readonly="1">
        <MessageSquareText className="h-3.5 w-3.5 text-sky-600 shrink-0" /><span className="flex-1 min-w-0" data-testid="dv3-brief-text">{p.statusBrief}</span>
        {p.statusBriefNextDate && <NextDateChip date={p.statusBriefNextDate} />}<span className="text-[10px] text-slate-400" data-testid="dv3-brief-meta">{when}</span>
      </div>
    ) : null;
  }
  return (
    <div className="rounded-lg border border-sky-200 bg-sky-50/60 px-2.5 py-2 space-y-1" data-testid="dv3-brief" data-state={rec ? "recording" : stt ? "transcribing" : polishing ? "polishing" : dirty ? "dirty" : "saved"}>
      <div className="flex-wrap md:flex-nowrap flex items-center gap-2">
        <MessageSquareText className="h-3.5 w-3.5 text-sky-600 shrink-0" />
        <input ref={inputRef} data-testid="dv3-brief-input" value={v} maxLength={300} disabled={lock} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); }}
          placeholder={T("一句话：项目现在到哪一步，下一步节点怎么安排…（可点麦克风口述）")} className="flex-1 min-w-0 bg-white border border-sky-200 rounded-md px-2.5 py-1.5 text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-300 disabled:opacity-60" />
        <button type="button" data-testid="dv3-brief-mic" data-state={rec ? "recording" : stt ? "transcribing" : "idle"} disabled={busy || stt || polishing} onClick={mic} title={rec ? T("点击停止 → 转写并 AI 整理") : T("语音输入（点击开始，再点停止）")}
          className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`shrink-0 inline-flex items-center gap-1 h-8 px-2.5 rounded-full text-xs font-bold transition-colors ${rec ? "bg-red-600 text-white animate-pulse" : "bg-sky-600 text-white hover:bg-sky-700"} disabled:opacity-60`)}>
          {stt ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : rec ? <Square className="h-3 w-3" /> : <Mic className="h-3.5 w-3.5" />}
          <span className="hidden sm:inline">{stt ? T("转写中…") : rec ? `${T("停止")} ${sec}s` : T("语音")}</span>
        </button>
        <button type="button" data-testid="dv3-brief-polish" disabled={lock || v.trim().length < 2} onClick={polish} title={T("AI 整理成一句话并识别下一步日期")}
          className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 shrink-0 inline-flex items-center gap-1 h-8 px-2 rounded-full text-xs font-bold bg-violet-100 text-violet-700 hover:bg-violet-200 disabled:opacity-50 transition-colors">
          {polishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}<span className="hidden sm:inline">{T("AI 整理")}</span>
        </button>
        <button type="button" data-testid="dv3-brief-save" disabled={!dirty || lock} onClick={save} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 shrink-0 inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-xs font-semibold bg-white border border-sky-300 text-sky-700 hover:bg-sky-100 disabled:opacity-50 transition-colors">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}<span className="hidden sm:inline">{T("保存")}</span>
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] text-slate-400" data-testid="dv3-brief-meta">
        <label className="inline-flex items-center gap-1 text-slate-500" data-testid="dv3-brief-next" data-set={nd ? "1" : "0"}><CalendarClock className="h-3 w-3 text-amber-500" />{T("下一步")}
          <input type="date" data-testid="dv3-brief-next-input" value={nd} disabled={lock} onChange={(e) => setNd(e.target.value)} className={`border rounded px-1 py-0.5 text-[10px] ${nd ? "border-amber-300 bg-amber-50 text-amber-800 font-bold" : "border-slate-200 bg-white"}`} />
          {nd && <button type="button" data-testid="dv3-brief-next-clear" onClick={() => setNd("")} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-slate-400 hover:text-red-600"><X className="h-3 w-3" /></button>}
        </label>
        <UsDateHint compact testid="dv3-brief-next-tz" />
        <span>{when ? T("更新于 {0}", [when]) : T("尚未填写 · 一句话说明「现在到哪一步 + 下一步节点」")}</span>
        {dirty && <span className="text-amber-600 font-semibold" data-testid="dv3-brief-dirty">{T("未保存 · 回车或点保存")}</span>}
        {raw && <span className="text-slate-400 truncate max-w-full" data-testid="dv3-brief-raw" title={raw}>{T("原话：{0}", [raw])}</span>}
      </div>
    </div>
  );
};

export const NextDateChip = ({ date, tid = "dv3-brief-next-chip" }) => {
  const days = Math.ceil((new Date(`${date}T00:00:00`) - new Date(new Date().toDateString())) / 86400000);
  const late = days < 0;
  return <span data-testid={tid} data-days={days} className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${late ? "bg-red-50 text-red-700 border-red-200" : days <= 3 ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-amber-50 text-amber-700 border-amber-200"}`}><CalendarClock className="h-3 w-3" />{T("下一步 {0}", [fmtD(date)])}{late ? T(" · 已过 {0} 天", [-days]) : days === 0 ? T(" · 今天") : T(" · {0} 天后", [days])}</span>;
};
